import React from 'react'
import { Button, CircularProgress, Typography, Stack, Divider } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { CanteenCartItem } from '../types'
import { FragmentType, graphql, useFragment } from '@/generated/gql'
import { numberFormat } from '@/helpers/numbers'
import { Currency, getCurrencySymbol } from '@caterdesk/utils--money'

const canteenCartFooterUserDataFragment = graphql(`
  fragment CanteenCartFooter_UserDataFragment on User {
    subsidySettings {
      unlimitedSubsidy
    }
    account {
      topUpEnabled
      userInterfaceSettings {
        hideCCPrices
      }
    }
    wallet {
      balance
    }
  }
`)

type Props = {
  cartItems: CanteenCartItem[]
  cartTotal: number
  onClose: () => void
  onCheckoutClicked: () => void
  loading: boolean
  currency: string
  orderSucceeded: boolean
  availableSubsidy: number
  userData: FragmentType<typeof canteenCartFooterUserDataFragment>
}

const Footer: React.FC<Props> = (props) => {
  const { t } = useTranslation()
  const {
    cartItems,
    cartTotal,
    onClose,
    loading,
    onCheckoutClicked,
    orderSucceeded,
    availableSubsidy,
    userData: userDataProp,
    currency,
  } = props
  const userData = useFragment(canteenCartFooterUserDataFragment, userDataProp)
  const unlimitedSubsidy = Boolean(userData.subsidySettings.unlimitedSubsidy)
  const userWalletBalance = userData.account?.topUpEnabled ? Number(userData.wallet.balance) : 0 // If top-up is disabled, wallet balance cannot be used
  const totalToPay = unlimitedSubsidy
    ? 0
    : Math.max(cartTotal - availableSubsidy - userWalletBalance, 0)
  const insufficientCredits =
    !unlimitedSubsidy && cartTotal > Number(availableSubsidy) + userWalletBalance
  const disableCheckoutButton =
    (insufficientCredits && !userData.account?.topUpEnabled) || cartItems.length === 0

  return (
    <>
      <Divider />
      <Stack direction="column" paddingX={3} paddingY={2}>
        {!userData.account?.userInterfaceSettings.hideCCPrices ? (
          <Stack width="100%" direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle2">{t('cc:totalToPay')}</Typography>
            <Typography variant="subtitle2">{`${getCurrencySymbol(currency as Currency)}${numberFormat(
              totalToPay,
            )}`}</Typography>
          </Stack>
        ) : null}
      </Stack>
      <Stack
        direction="column"
        paddingX={3}
        paddingY={2}
        bgcolor="background.paper"
        data-cy="checkout-button"
      >
        <Button
          size="large"
          disabled={disableCheckoutButton || loading || orderSucceeded}
          variant="contained"
          fullWidth
          onClick={onCheckoutClicked}
        >
          {loading ? <CircularProgress size={24} /> : t('cc:checkout')}
        </Button>
        <Button size="large" fullWidth variant="text" onClick={onClose}>
          {t('cc:backToMenu')}
        </Button>
      </Stack>
    </>
  )
}

export default Footer
